import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { sb } from '../lib/supabase';
import { fmt, stageBadgeClass, statusBadgeClass } from '../lib/helpers';
import Sidebar from '../components/Sidebar';
import Wordmark from '../components/Wordmark';
import WorkspaceFrame from '../components/WorkspaceFrame';
import { useRexScreen } from '../context/RexContext';
import Badge from '../components/Badge';
import Loader from '../components/Loader';
import { useSupabaseAuth, useAccess, useProgrammeAccess } from '../context/AuthContext';
import { parseUiPrefs } from '../lib/userPreferences';

/** Must render inside WorkspaceFrame → RexProvider */
function HomeRexSync({ programmes, projects, selectedProg, programmeFilter }) {
  const snapshot = useMemo(() => ({
    programmeCount: programmes.length,
    projectCount: projects.length,
    selectedProgramme: selectedProg?.name,
    filter: programmeFilter,
  }), [programmes.length, projects.length, selectedProg?.name, programmeFilter]);
  useRexScreen({ snapshot });
  return null;
}

export default function Home() {
  const navigate = useNavigate();
  const { profile, refreshSupabaseSession } = useSupabaseAuth();
  const { isAdmin: orgAdmin } = useAccess();
  const [programmes, setProgrammes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProg, setSelectedProg] = useState(null);
  const [dbStatus, setDbStatus] = useState('connecting'); // connecting | ok | error
  const [dbError, setDbError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showProgrammeModal, setShowProgrammeModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showEditProgrammeModal, setShowEditProgrammeModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showDeleteProgrammeModal, setShowDeleteProgrammeModal] = useState(false);
  const [newProgrammeName, setNewProgrammeName] = useState('');
  const [newProgrammeDesc, setNewProgrammeDesc] = useState('');
  const [newProgrammeStatusId, setNewProgrammeStatusId] = useState('');
  const [newProgrammeStageId, setNewProgrammeStageId] = useState('');
  const [newProgrammeStartDate, setNewProgrammeStartDate] = useState('');
  const [newProgrammeTargetDate, setNewProgrammeTargetDate] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [newProjectStatusId, setNewProjectStatusId] = useState('');
  const [newProjectStageId, setNewProjectStageId] = useState('');
  const [newProjectStartDate, setNewProjectStartDate] = useState('');
  const [newProjectTargetDate, setNewProjectTargetDate] = useState('');
  const [statusOptions, setStatusOptions] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);
  const [createError, setCreateError] = useState('');
  const [editError, setEditError] = useState('');
  const [savingProgramme, setSavingProgramme] = useState(false);
  const [savingProject, setSavingProject] = useState(false);
  const [savingProgrammeEdit, setSavingProgrammeEdit] = useState(false);
  const [savingProjectEdit, setSavingProjectEdit] = useState(false);
  const [savingProgrammeDelete, setSavingProgrammeDelete] = useState(false);
  const [programmeFilter, setProgrammeFilter] = useState('active'); // active | closed
  const selectedProgAccess = useProgrammeAccess(selectedProg?.id ?? null);
  const canEditProgramme = selectedProg && (orgAdmin || selectedProgAccess.canWrite);
  const canDeleteProgramme = selectedProg && (orgAdmin || selectedProgAccess.isAdmin);
  const canCreateProject = selectedProg && (orgAdmin || selectedProgAccess.canWrite);

  useEffect(() => {
    const prefs = parseUiPrefs(profile?.ui_prefs);
    setProgrammeFilter(prefs.programme_filter === 'closed' ? 'closed' : 'active');
  }, [profile?.ui_prefs]);
  const [editProgrammeId, setEditProgrammeId] = useState('');
  const [editProgrammeName, setEditProgrammeName] = useState('');
  const [editProgrammeDesc, setEditProgrammeDesc] = useState('');
  const [editProgrammeStatusId, setEditProgrammeStatusId] = useState('');
  const [editProgrammeStageId, setEditProgrammeStageId] = useState('');
  const [editProgrammeStartDate, setEditProgrammeStartDate] = useState('');
  const [editProgrammeTargetDate, setEditProgrammeTargetDate] = useState('');
  const [editProjectId, setEditProjectId] = useState('');
  const [editProjectName, setEditProjectName] = useState('');
  const [editProjectDesc, setEditProjectDesc] = useState('');
  const [editProjectStatusId, setEditProjectStatusId] = useState('');
  const [editProjectStageId, setEditProjectStageId] = useState('');
  const [editProjectStartDate, setEditProjectStartDate] = useState('');
  const [editProjectTargetDate, setEditProjectTargetDate] = useState('');

  const pickDefaultStatusId = useCallback(() => (statusOptions[0]?.id ? String(statusOptions[0].id) : ''), [statusOptions]);
  const pickDefaultStageId = useCallback(() => (stageOptions[0]?.id ? String(stageOptions[0].id) : ''), [stageOptions]);
  const isClosedStatus = useCallback((statusName) => String(statusName || '').toLowerCase().includes('closed'), []);

  const loadDashboardData = useCallback(async (isRetry = false) => {
    try {
      let loadedProgrammes = [];
      let loadedProjects = [];
      const [{ data: progs, error: programmesError }, { data: projs, error: projectsError }] = await Promise.all([
        sb.from('programmes').select('*, organisations(name), programme_statuses(name), programme_stages(name)').order('created_at'),
        sb.from('projects').select('*, programme_statuses(name), programme_stages(name)').order('created_at'),
      ]);
      const [{ data: statuses }, { data: stages }] = await Promise.all([
        sb.from('programme_statuses').select('id, name, order_index').order('order_index', { ascending: true }),
        sb.from('programme_stages').select('id, name, order_index').order('order_index', { ascending: true }),
      ]);
      // If relationship joins fail (common during schema/RLS transitions), retry with base tables.
      if (programmesError || projectsError) {
        const [{ data: fallbackProgs, error: fallbackProgrammesError }, { data: fallbackProjs, error: fallbackProjectsError }] = await Promise.all([
          sb.from('programmes').select('*').order('created_at'),
          sb.from('projects').select('*').order('created_at'),
        ]);

        if (fallbackProgrammesError || fallbackProjectsError) {
          throw fallbackProgrammesError || fallbackProjectsError;
        }

        loadedProgrammes = fallbackProgs || [];
        loadedProjects = fallbackProjs || [];
      } else {
        loadedProgrammes = progs || [];
        loadedProjects = projs || [];
      }

      setProgrammes(loadedProgrammes);
      setProjects(loadedProjects);
      setStatusOptions(statuses || []);
      setStageOptions(stages || []);
      setDbStatus('ok');
      setDbError('');
      setSelectedProg((prev) => {
        if (!loadedProgrammes.length) return null;
        if (prev && loadedProgrammes.some((p) => p.id === prev.id)) return prev;
        return loadedProgrammes[0];
      });
    } catch (e) {
      const msg = e?.message || String(e);
      if (!isRetry && /jwt expired/i.test(msg)) {
        const renewed = await refreshSupabaseSession();
        if (renewed) {
          await loadDashboardData(true);
          return;
        }
      }
      setDbStatus('error');
      setDbError(msg);
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [refreshSupabaseSession]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const filteredProgrammes = useMemo(
    () =>
      programmes.filter((p) => {
        const closed = isClosedStatus(p?.programme_statuses?.name);
        return programmeFilter === 'closed' ? closed : !closed;
      }),
    [programmes, programmeFilter, isClosedStatus]
  );

  useEffect(() => {
    if (!filteredProgrammes.length) {
      setSelectedProg(null);
      return;
    }
    if (!selectedProg || !filteredProgrammes.some((p) => p.id === selectedProg.id)) {
      setSelectedProg(filteredProgrammes[0]);
    }
  }, [filteredProgrammes, selectedProg]);

  async function handleCreateProgramme() {
    const name = newProgrammeName.trim();
    if (!name) {
      setCreateError('Programme name is required.');
      return;
    }
    if (!profile?.organisation_id) {
      setCreateError('Your profile is missing an organisation.');
      return;
    }
    if (newProgrammeStartDate && newProgrammeTargetDate && newProgrammeTargetDate < newProgrammeStartDate) {
      setCreateError('Target go-live must be on or after the start date.');
      return;
    }

    setSavingProgramme(true);
    setCreateError('');
    try {
      const { data, error } = await sb
        .from('programmes')
        .insert({
          name,
          description: newProgrammeDesc.trim() || null,
          organisation_id: profile.organisation_id,
          status_id: newProgrammeStatusId || null,
          stage_id: newProgrammeStageId || null,
          start_date: newProgrammeStartDate || null,
          target_go_live: newProgrammeTargetDate || null,
        })
        .select('id')
        .single();
      if (error) throw error;
      await loadDashboardData();
      if (data?.id) {
        const created = (await sb.from('programmes').select('*').eq('id', data.id).maybeSingle()).data;
        if (created) setSelectedProg(created);
      }
      setShowProgrammeModal(false);
      setNewProgrammeName('');
      setNewProgrammeDesc('');
      setNewProgrammeStatusId('');
      setNewProgrammeStageId('');
      setNewProgrammeStartDate('');
      setNewProgrammeTargetDate('');
    } catch (e) {
      setCreateError(e?.message || 'Could not create programme.');
    } finally {
      setSavingProgramme(false);
    }
  }

  async function handleCreateProject() {
    const name = newProjectName.trim();
    if (!name) {
      setCreateError('Project name is required.');
      return;
    }
    if (!selectedProg?.id) {
      setCreateError('Select a programme first.');
      return;
    }
    if (newProjectStartDate && newProjectTargetDate && newProjectTargetDate < newProjectStartDate) {
      setCreateError('Target go-live must be on or after the start date.');
      return;
    }

    setSavingProject(true);
    setCreateError('');
    try {
      const { error } = await sb.from('projects').insert({
        name,
        description: newProjectDesc.trim() || null,
        programme_id: selectedProg.id,
        status_id: newProjectStatusId || null,
        stage_id: newProjectStageId || null,
        start_date: newProjectStartDate || null,
        target_go_live: newProjectTargetDate || null,
      });
      if (error) throw error;
      await loadDashboardData();
      setShowProjectModal(false);
      setNewProjectName('');
      setNewProjectDesc('');
      setNewProjectStatusId('');
      setNewProjectStageId('');
      setNewProjectStartDate('');
      setNewProjectTargetDate('');
    } catch (e) {
      setCreateError(e?.message || 'Could not create project.');
    } finally {
      setSavingProject(false);
    }
  }

  function openEditProgrammeModal(prog) {
    if (!prog?.id) return;
    setEditError('');
    setEditProgrammeId(prog.id);
    setEditProgrammeName(prog.name || '');
    setEditProgrammeDesc(prog.description || '');
    setEditProgrammeStatusId(prog.status_id || '');
    setEditProgrammeStageId(prog.stage_id || '');
    setEditProgrammeStartDate(prog.start_date || '');
    setEditProgrammeTargetDate(prog.target_go_live || '');
    setShowEditProgrammeModal(true);
  }

  function openEditProjectModal(proj) {
    if (!proj?.id) return;
    setEditError('');
    setEditProjectId(proj.id);
    setEditProjectName(proj.name || '');
    setEditProjectDesc(proj.description || '');
    setEditProjectStatusId(proj.status_id || '');
    setEditProjectStageId(proj.stage_id || '');
    setEditProjectStartDate(proj.start_date || '');
    setEditProjectTargetDate(proj.target_go_live || '');
    setShowEditProjectModal(true);
  }

  async function handleUpdateProgramme() {
    if (!editProgrammeId) return;
    const name = editProgrammeName.trim();
    if (!name) {
      setEditError('Programme name is required.');
      return;
    }
    if (editProgrammeStartDate && editProgrammeTargetDate && editProgrammeTargetDate < editProgrammeStartDate) {
      setEditError('Target go-live must be on or after the start date.');
      return;
    }
    setSavingProgrammeEdit(true);
    setEditError('');
    try {
      const { error } = await sb
        .from('programmes')
        .update({
          name,
          description: editProgrammeDesc.trim() || null,
          status_id: editProgrammeStatusId || null,
          stage_id: editProgrammeStageId || null,
          start_date: editProgrammeStartDate || null,
          target_go_live: editProgrammeTargetDate || null,
        })
        .eq('id', editProgrammeId);
      if (error) throw error;
      await loadDashboardData();
      setShowEditProgrammeModal(false);
    } catch (e) {
      setEditError(e?.message || 'Could not update programme.');
    } finally {
      setSavingProgrammeEdit(false);
    }
  }

  async function handleUpdateProject() {
    if (!editProjectId) return;
    const name = editProjectName.trim();
    if (!name) {
      setEditError('Project name is required.');
      return;
    }
    if (editProjectStartDate && editProjectTargetDate && editProjectTargetDate < editProjectStartDate) {
      setEditError('Target go-live must be on or after the start date.');
      return;
    }
    setSavingProjectEdit(true);
    setEditError('');
    try {
      const { error } = await sb
        .from('projects')
        .update({
          name,
          description: editProjectDesc.trim() || null,
          status_id: editProjectStatusId || null,
          stage_id: editProjectStageId || null,
          start_date: editProjectStartDate || null,
          target_go_live: editProjectTargetDate || null,
        })
        .eq('id', editProjectId);
      if (error) throw error;
      await loadDashboardData();
      setShowEditProjectModal(false);
    } catch (e) {
      setEditError(e?.message || 'Could not update project.');
    } finally {
      setSavingProjectEdit(false);
    }
  }

  async function handleDeleteProgramme() {
    if (!selectedProg?.id) return;
    setSavingProgrammeDelete(true);
    setEditError('');
    try {
      const { error } = await sb.from('programmes').delete().eq('id', selectedProg.id);
      if (error) throw error;
      setShowDeleteProgrammeModal(false);
      await loadDashboardData();
    } catch (e) {
      setEditError(e?.message || 'Could not delete programme.');
    } finally {
      setSavingProgrammeDelete(false);
    }
  }

  const filteredProjects = projects.filter(p => p.programme_id === selectedProg?.id);
  const orgName = programmes[0]?.organisations?.name || 'Organisation';

  return (
    <div className="screen active">
      <Sidebar />
      <div className="main">
        <WorkspaceFrame rexBase={{ layer: 'portfolio', orgName }}>
        <HomeRexSync
          programmes={programmes}
          projects={projects}
          selectedProg={selectedProg}
          programmeFilter={programmeFilter}
        />
        <div className="topbar">
          <div>
            <div className="tbtitle">Welcome to Transformax</div>
            <div className="tbcrumb">Select a programme and project to begin</div>
          </div>
          <div className="badges">
            <Badge>{programmes.length} programme{programmes.length !== 1 ? 's' : ''}</Badge>
            <Badge>{projects.length} project{projects.length !== 1 ? 's' : ''}</Badge>
          </div>
        </div>

        <div className="content">
          {/* Hero */}
          <div className="hero">
            <div>
              <Wordmark className="logo-mark hero-wordmark" />
              <p>Your end-to-end transformation programme platform — governance, management, workbench and financial reporting in one place.</p>
              <div className="ppills">
                <span className="ppill" style={{ background: '#eeedfe', color: '#3c3489', borderColor: '#afa9ec' }}>Governance</span>
                <span className="ppill" style={{ background: '#e1f5ee', color: '#085041', borderColor: '#5dcaa5' }}>Management</span>
                <span className="ppill" style={{ background: '#faeeda', color: '#633806', borderColor: '#fac775' }}>Workbench</span>
                <span className="ppill" style={{ background: '#E6F1FB', color: '#0C447C', borderColor: '#85B7EB' }}>Finance & reporting</span>
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 11, color: '#888780', marginBottom: 6 }}>Database</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: dbStatus === 'ok' ? '#1D9E75' : dbStatus === 'error' ? '#E24B4A' : '#d3d1c7' }} />
                <span style={{ fontSize: 11, color: dbStatus === 'ok' ? '#085041' : dbStatus === 'error' ? '#A32D2D' : '#888780' }}>
                  {dbStatus === 'ok' ? 'Live data' : dbStatus === 'error' ? 'Connection error' : 'Connecting…'}
                </span>
              </div>
              {dbStatus === 'error' && dbError && (
                <div style={{ marginTop: 6, maxWidth: 260, fontSize: 10, color: '#A32D2D', textAlign: 'right' }}>
                  {dbError}
                </div>
              )}
            </div>
          </div>

          {/* Programmes */}
          <div>
            <div className="sec-hdr">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="sec-title">Programmes</span>
                <div className="filter-tabs" style={{ marginBottom: 0 }}>
                  <button className={`ftab ${programmeFilter === 'active' ? 'active' : ''}`} onClick={() => setProgrammeFilter('active')}>
                    Active
                  </button>
                  <button className={`ftab ${programmeFilter === 'closed' ? 'active' : ''}`} onClick={() => setProgrammeFilter('closed')}>
                    Closed
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="sec-act"
                  onClick={() => {
                    setEditError('');
                    setShowDeleteProgrammeModal(true);
                  }}
                  disabled={!canDeleteProgramme}
                  style={{ opacity: canDeleteProgramme ? 1 : 0.45, color: '#A32D2D' }}
                >
                  Delete selected
                </button>
                <button
                  className="sec-act"
                  onClick={() => selectedProg && openEditProgrammeModal(selectedProg)}
                  disabled={!canEditProgramme}
                  style={{ opacity: canEditProgramme ? 1 : 0.45 }}
                >
                  Edit selected
                </button>
                <button
                  className="sec-act"
                  onClick={() => {
                    setCreateError('');
                    setNewProgrammeStatusId(pickDefaultStatusId());
                    setNewProgrammeStageId(pickDefaultStageId());
                    setNewProgrammeStartDate('');
                    setNewProgrammeTargetDate('');
                    setShowProgrammeModal(true);
                  }}
                >
                  + New programme
                </button>
              </div>
            </div>
            {loading ? (
              <Loader />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 10 }}>
                {filteredProgrammes.map(p => {
                  const projCount = projects.filter(j => j.programme_id === p.id).length;
                  const statusName = p.programme_statuses?.name || 'Draft';
                  const stageName  = p.programme_stages?.name  || '—';
                  return (
                    <div key={p.id} className={`prog-card ${selectedProg?.id === p.id ? 'sel' : ''}`} onClick={() => setSelectedProg(p)}>
                      <div className="phdr">
                        <div>
                          <div className="pname">{p.name}</div>
                          <div className="porg">{p.organisations?.name}</div>
                        </div>
                        <Badge variant={statusBadgeClass(statusName)}>{statusName}</Badge>
                      </div>
                      <div className="pmeta" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <Badge variant="pu">{projCount} project{projCount !== 1 ? 's' : ''}</Badge>
                          <Badge>{stageName}</Badge>
                        </div>
                        <button
                          className="sec-act"
                          onClick={(e) => { e.stopPropagation(); navigate(`/programme/${p.id}`); }}
                        >
                          Open →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Projects */}
          {selectedProg && (
            <div>
              <div className="sec-hdr">
                <span className="sec-title">Projects — {selectedProg.name}</span>
                <button
                  className="sec-act"
                  onClick={() => {
                    setCreateError('');
                    setNewProjectStatusId(pickDefaultStatusId());
                    setNewProjectStageId(pickDefaultStageId());
                    setNewProjectStartDate('');
                    setNewProjectTargetDate('');
                    setShowProjectModal(true);
                  }}
                  disabled={!canCreateProject}
                  style={{ opacity: canCreateProject ? 1 : 0.45 }}
                >
                  + New project
                </button>
              </div>
              {filteredProjects.length === 0 ? (
                <div className="empty">No projects found for this programme.</div>
              ) : (
                filteredProjects.map(p => {
                  const stageName  = p.programme_stages?.name  || '—';
                  const statusName = p.programme_statuses?.name || '—';
                  return (
                    <div key={p.id} className="proj-row" onClick={() => navigate(`/project/${p.id}`)}>
                      <div>
                        <div className="pjname">{p.name}</div>
                        <div style={{ fontSize: 11, color: '#888780', marginTop: 2 }}>
                          {selectedProg.name}{p.target_go_live ? ' · Go-live: ' + fmt(p.target_go_live) : ''}
                        </div>
                      </div>
                      <div className="pji">
                        <Badge variant={stageBadgeClass(stageName)}>{stageName}</Badge>
                        <Badge variant={statusBadgeClass(statusName)}>{statusName}</Badge>
                        {canCreateProject && (
                        <button
                          className="sec-act"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditProjectModal(p);
                          }}
                        >
                          Edit
                        </button>
                        )}
                        <span className="parr">Open →</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
        </WorkspaceFrame>
      </div>
      {showProgrammeModal && (
        <div className="modal-overlay" onClick={() => !savingProgramme && setShowProgrammeModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-hdr">
              <span>Create programme</span>
              <button className="modal-close" onClick={() => !savingProgramme && setShowProgrammeModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label className="form-label">Programme name</label>
                <input
                  className="form-input"
                  value={newProgrammeName}
                  onChange={(e) => setNewProgrammeName(e.target.value)}
                  placeholder="e.g. SAP S/4HANA transformation"
                />
              </div>
              <div className="form-row">
                <label className="form-label">Description (optional)</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={newProgrammeDesc}
                  onChange={(e) => setNewProgrammeDesc(e.target.value)}
                  placeholder="Context, scope, and expected outcomes"
                />
              </div>
              <div className="form-2col">
                <div className="form-row">
                  <label className="form-label">Status (optional)</label>
                  <select className="form-select" value={newProgrammeStatusId} onChange={(e) => setNewProgrammeStatusId(e.target.value)}>
                    <option value="">Select status</option>
                    {statusOptions.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label className="form-label">Stage (optional)</label>
                  <select className="form-select" value={newProgrammeStageId} onChange={(e) => setNewProgrammeStageId(e.target.value)}>
                    <option value="">Select stage</option>
                    {stageOptions.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-2col">
                <div className="form-row">
                  <label className="form-label">Start date (optional)</label>
                  <input className="form-input" type="date" value={newProgrammeStartDate} onChange={(e) => setNewProgrammeStartDate(e.target.value)} />
                </div>
                <div className="form-row">
                  <label className="form-label">Target go-live (optional)</label>
                  <input className="form-input" type="date" value={newProgrammeTargetDate} onChange={(e) => setNewProgrammeTargetDate(e.target.value)} />
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#888780' }}>
                Target go-live must be on or after the start date.
              </div>
            </div>
            {createError && <div className="form-error" role="alert">{createError}</div>}
            <div className="modal-ftr">
              <button className="btn-ghost" onClick={() => setShowProgrammeModal(false)} disabled={savingProgramme}>Cancel</button>
              <button className="btn-primary" onClick={handleCreateProgramme} disabled={savingProgramme}>
                {savingProgramme ? 'Creating…' : 'Create programme'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showProjectModal && (
        <div className="modal-overlay" onClick={() => !savingProject && setShowProjectModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-hdr">
              <span>Create project</span>
              <button className="modal-close" onClick={() => !savingProject && setShowProjectModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label className="form-label">Programme</label>
                <input className="form-input" value={selectedProg?.name || ''} disabled />
              </div>
              <div className="form-row">
                <label className="form-label">Project name</label>
                <input
                  className="form-input"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="e.g. UK rollout"
                />
              </div>
              <div className="form-row">
                <label className="form-label">Description (optional)</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={newProjectDesc}
                  onChange={(e) => setNewProjectDesc(e.target.value)}
                  placeholder="Region, timeline, and objectives"
                />
              </div>
              <div className="form-2col">
                <div className="form-row">
                  <label className="form-label">Status (optional)</label>
                  <select className="form-select" value={newProjectStatusId} onChange={(e) => setNewProjectStatusId(e.target.value)}>
                    <option value="">Select status</option>
                    {statusOptions.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label className="form-label">Stage (optional)</label>
                  <select className="form-select" value={newProjectStageId} onChange={(e) => setNewProjectStageId(e.target.value)}>
                    <option value="">Select stage</option>
                    {stageOptions.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-2col">
                <div className="form-row">
                  <label className="form-label">Start date (optional)</label>
                  <input className="form-input" type="date" value={newProjectStartDate} onChange={(e) => setNewProjectStartDate(e.target.value)} />
                </div>
                <div className="form-row">
                  <label className="form-label">Target go-live (optional)</label>
                  <input className="form-input" type="date" value={newProjectTargetDate} onChange={(e) => setNewProjectTargetDate(e.target.value)} />
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#888780' }}>
                Target go-live must be on or after the start date.
              </div>
            </div>
            {createError && <div className="form-error" role="alert">{createError}</div>}
            <div className="modal-ftr">
              <button className="btn-ghost" onClick={() => setShowProjectModal(false)} disabled={savingProject}>Cancel</button>
              <button className="btn-primary" onClick={handleCreateProject} disabled={savingProject || !selectedProg?.id}>
                {savingProject ? 'Creating…' : 'Create project'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showEditProgrammeModal && (
        <div className="modal-overlay" onClick={() => !savingProgrammeEdit && setShowEditProgrammeModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-hdr">
              <span>Edit programme</span>
              <button className="modal-close" onClick={() => !savingProgrammeEdit && setShowEditProgrammeModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label className="form-label">Programme name</label>
                <input className="form-input" value={editProgrammeName} onChange={(e) => setEditProgrammeName(e.target.value)} />
              </div>
              <div className="form-row">
                <label className="form-label">Description (optional)</label>
                <textarea className="form-textarea" rows={3} value={editProgrammeDesc} onChange={(e) => setEditProgrammeDesc(e.target.value)} />
              </div>
              <div className="form-2col">
                <div className="form-row">
                  <label className="form-label">Status (optional)</label>
                  <select className="form-select" value={editProgrammeStatusId} onChange={(e) => setEditProgrammeStatusId(e.target.value)}>
                    <option value="">Select status</option>
                    {statusOptions.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label className="form-label">Stage (optional)</label>
                  <select className="form-select" value={editProgrammeStageId} onChange={(e) => setEditProgrammeStageId(e.target.value)}>
                    <option value="">Select stage</option>
                    {stageOptions.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-2col">
                <div className="form-row">
                  <label className="form-label">Start date (optional)</label>
                  <input className="form-input" type="date" value={editProgrammeStartDate} onChange={(e) => setEditProgrammeStartDate(e.target.value)} />
                </div>
                <div className="form-row">
                  <label className="form-label">Target go-live (optional)</label>
                  <input className="form-input" type="date" value={editProgrammeTargetDate} onChange={(e) => setEditProgrammeTargetDate(e.target.value)} />
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#888780' }}>
                Target go-live must be on or after the start date.
              </div>
            </div>
            {editError && <div className="form-error" role="alert">{editError}</div>}
            <div className="modal-ftr">
              <button className="btn-ghost" onClick={() => setShowEditProgrammeModal(false)} disabled={savingProgrammeEdit}>Cancel</button>
              <button className="btn-primary" onClick={handleUpdateProgramme} disabled={savingProgrammeEdit}>
                {savingProgrammeEdit ? 'Saving…' : 'Save programme'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showEditProjectModal && (
        <div className="modal-overlay" onClick={() => !savingProjectEdit && setShowEditProjectModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-hdr">
              <span>Edit project</span>
              <button className="modal-close" onClick={() => !savingProjectEdit && setShowEditProjectModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label className="form-label">Project name</label>
                <input className="form-input" value={editProjectName} onChange={(e) => setEditProjectName(e.target.value)} />
              </div>
              <div className="form-row">
                <label className="form-label">Description (optional)</label>
                <textarea className="form-textarea" rows={3} value={editProjectDesc} onChange={(e) => setEditProjectDesc(e.target.value)} />
              </div>
              <div className="form-2col">
                <div className="form-row">
                  <label className="form-label">Status (optional)</label>
                  <select className="form-select" value={editProjectStatusId} onChange={(e) => setEditProjectStatusId(e.target.value)}>
                    <option value="">Select status</option>
                    {statusOptions.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label className="form-label">Stage (optional)</label>
                  <select className="form-select" value={editProjectStageId} onChange={(e) => setEditProjectStageId(e.target.value)}>
                    <option value="">Select stage</option>
                    {stageOptions.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-2col">
                <div className="form-row">
                  <label className="form-label">Start date (optional)</label>
                  <input className="form-input" type="date" value={editProjectStartDate} onChange={(e) => setEditProjectStartDate(e.target.value)} />
                </div>
                <div className="form-row">
                  <label className="form-label">Target go-live (optional)</label>
                  <input className="form-input" type="date" value={editProjectTargetDate} onChange={(e) => setEditProjectTargetDate(e.target.value)} />
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#888780' }}>
                Target go-live must be on or after the start date.
              </div>
            </div>
            {editError && <div className="form-error" role="alert">{editError}</div>}
            <div className="modal-ftr">
              <button className="btn-ghost" onClick={() => setShowEditProjectModal(false)} disabled={savingProjectEdit}>Cancel</button>
              <button className="btn-primary" onClick={handleUpdateProject} disabled={savingProjectEdit}>
                {savingProjectEdit ? 'Saving…' : 'Save project'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteProgrammeModal && (
        <div className="modal-overlay" onClick={() => !savingProgrammeDelete && setShowDeleteProgrammeModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-hdr">
              <span>Delete programme</span>
              <button className="modal-close" onClick={() => !savingProgrammeDelete && setShowDeleteProgrammeModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: '#5f5e5a', lineHeight: 1.6 }}>
                This action is permanent. Deleting <strong>{selectedProg?.name || 'this programme'}</strong> will also
                delete dependent records such as projects (via database cascade).
              </div>
            </div>
            {editError && <div className="form-error" role="alert">{editError}</div>}
            <div className="modal-ftr">
              <button className="btn-ghost" onClick={() => setShowDeleteProgrammeModal(false)} disabled={savingProgrammeDelete}>Cancel</button>
              <button className="btn-primary" style={{ background: '#A32D2D' }} onClick={handleDeleteProgramme} disabled={savingProgrammeDelete}>
                {savingProgrammeDelete ? 'Deleting…' : 'Yes, delete programme'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
